/**
 * Client des projets.
 *
 * Un projet est l'unité de travail : on le nomme — « Mesure dimensionnelle machine
 * KMC4 » — puis on y dépose des fichiers, dont les mesures s'accumulent dans la même
 * série. Le projet porte la continuité ; le format des fichiers a le droit d'évoluer.
 */

import { apiFetch, detailOf } from "./http";
import type { AnalysedChart, AnalysisWindow } from "./mappingApi";

export interface Project {
  id: string;
  name: string;
  kind: string;
  created_at: string;
  files_count: number;
  measurements_count: number;
  characteristics_count: number;
  /** `null` pour un projet créé mais jamais alimenté. État normal, pas une anomalie. */
  last_deposit_at: string | null;
}

export interface Deposit {
  id: string;
  filename: string;
  received_at: string;
  measurements_written: number;
  rows_rejected: number;
  status: string;
  /** Change quand le format de la machine a dérivé : l'historique le rend lisible. */
  template_version: number | null;
}

export interface ProjectCharacteristic {
  id: string;
  code: string;
  name: string;
  measurements_count: number;
  first_ts: string | null;
  last_ts: string | null;
  /** Tolérances en vigueur aujourd'hui, telles qu'elles sont enregistrées. */
  lsl: number | null;
  usl: number | null;
  target: number | null;
}

export interface ProjectDetail {
  project: Project;
  deposits: Deposit[];
  characteristics: ProjectCharacteristic[];
}

export interface ProjectSeries {
  charts: AnalysedChart[];
  charts_unavailable: Record<string, string>;
  reason: string | null;
}

/** Statuts d'un dépôt, tels que la base les contraint. */
export const DEPOSIT_STATUS: Record<string, string> = {
  received: "reçu",
  parsing: "en cours de lecture",
  done: "intégré",
  partial: "intégré partiellement",
  quarantined: "en quarantaine",
  failed: "en échec",
};

export async function listProjects(): Promise<Project[]> {
  const response = await apiFetch("/v1/projects");
  if (!response.ok) throw new Error(await detailOf(response));
  return (await response.json()) as Project[];
}

/**
 * Crée un projet vide.
 *
 * Le 409 est renvoyé tel quel dans le message : « un projet nommé X existe déjà » est
 * actionnable — l'utilisateur le choisit dans la liste — alors qu'un échec muet le
 * laisserait retaper le même nom.
 */
export async function createProject(name: string): Promise<Project> {
  const response = await apiFetch("/v1/projects", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) throw new Error(await detailOf(response));
  return (await response.json()) as Project;
}

export async function getProject(id: string): Promise<ProjectDetail> {
  const response = await apiFetch(`/v1/projects/${id}`);
  if (!response.ok) throw new Error(await detailOf(response));
  return (await response.json()) as ProjectDetail;
}

/**
 * Trace les cartes d'un projet depuis ses mesures en base.
 *
 * En POST malgré une sémantique de lecture : la fenêtre d'analyse est un objet
 * structuré, et l'encoder dans une URL la rendrait illisible dans les journaux — là
 * précisément où on la relit pour comprendre un chiffre contesté. Rien n'est écrit.
 */
export async function getProjectSeries(
  id: string,
  window: AnalysisWindow,
  code?: string,
  natures: Record<string, string> = {},
): Promise<ProjectSeries> {
  const response = await apiFetch(`/v1/projects/${id}/series`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ window, code: code ?? null, natures }),
  });
  if (!response.ok) throw new Error(await detailOf(response));
  return (await response.json()) as ProjectSeries;
}

/** Format court et non ambigu. « 14 août, 08:12 » se lit d'un coup d'œil ; un ISO non. */
export function frenchDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
